// Missed-instalment chase + 7-day split (tv-qta, Feature B). On a MISSED top-up
// the Agent runs a full 3-sequence chase to retain the booking (vault Diagram 02,
// settled 18 Aug): prefer chasing an existing client to refilling the spot.
//
//   Reached -> pays          the instalment clears; the plan continues.
//   Reached -> wants to move  outside 7d the agent moves it directly + takes a
//                             partial payment; inside 7d it needs Ops approval.
//   No pickup after 3 goes -> CANCELLED, then the 7-day split on the cancel:
//                             inside 7d  -> FORFEIT (a new deposit is needed);
//                             outside 7d -> KEEP as a deposit-no-date (12 months)
//                                           -> the Sweeper pool.
//
// Domain logic only: this is NOT surfaced into the agent queue or any screen -
// Wave 2 (tv-9vr) does the queue surfacing (which touches the queue files Wave 0
// owns). The cancel reuses W5's applyCancelSplit (@atink/domain). "Now" is the sim clock,
// never Date.now(); money is whole AUD dollars.

import { isInsideCancelWindow } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { currentActorId } from '@/domain/actor';
import { cancelTopupPlanById } from '@/domain/opsCancelFlow';
import { isTopupPlanComplete } from '@/domain/topupPlan';
import { depositPaidAud } from '@/utils/bookingSummary';
import { topupMoveDateIntent, type TopupMovePay } from '@/api/intents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { mirrorCapturedEmail } from '@/domain/depositFlow';
import type { Booking, DepositLedgerEntry, TopupInstalment, TopupPlan } from '@/domain/types';

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

interface FlowOpts {
  by?: string;
  at?: string;
}

/** How many unanswered chase sequences end the retention effort and cancel. */
export const MAX_CHASE_SEQUENCES = 3;

/**
 * Which side of the 7-day line the plan's booking sits on, measured from `at`
 * against the booking's appointment date (`booking.start`). A dateless plan (no
 * bookingId, or a booking that has gone missing) cannot be inside 7 days, so it
 * defaults to 'outside'. Whole calendar days, so it flips on the day boundary
 * exactly like the confirmation-cancel split (vault Diagram 06).
 */
export function topupSevenDaySplit(plan: TopupPlan, at: string): 'inside' | 'outside' {
  const booking = plan.bookingId ? useBrainStore.getState().bookings[plan.bookingId] : undefined;
  if (!booking) return 'outside';
  return isInsideCancelWindow(booking.start, at) ? 'inside' : 'outside';
}

/** The index of the instalment the chase is about: the earliest `missed`, else
 *  the earliest `reminded` (the reconciler marks a miss from a reminded one). */
function currentChaseIndex(plan: TopupPlan): number {
  const missed = plan.instalments.findIndex((i) => i.status === 'missed');
  if (missed !== -1) return missed;
  return plan.instalments.findIndex((i) => i.status === 'reminded');
}

/**
 * Reached -> the client pays the currently-missed instalment. Marks the earliest
 * `missed` (fallback `reminded`) instalment `paid` (+ paidAt), appends a `topup`
 * ledger entry to the deposit so its paid balance grows by the instalment amount,
 * resets chaseAttempts to 0, and flips the plan to `completed` once every
 * instalment is settled. Idempotent no-op on an already-settled plan. Publishes
 * `topup:chase-paid`.
 */
export function topupChasePays(planId: string, opts?: FlowOpts): { ok: boolean } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false };
  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'system';
  if (plan.status !== 'active') return { ok: true }; // already completed / cancelled

  const idx = currentChaseIndex(plan);
  if (idx === -1) return { ok: false }; // nothing missed or reminded to pay

  const instalment = plan.instalments[idx]!;
  const instalments: TopupInstalment[] = plan.instalments.map((inst, i) =>
    i === idx ? { ...inst, status: 'paid', paidAt: at } : inst,
  );
  const nextPlan: TopupPlan = { ...plan, instalments, chaseAttempts: 0 };
  const status: TopupPlan['status'] = isTopupPlanComplete(nextPlan) ? 'completed' : plan.status;
  brain.upsertTopupPlan({ ...nextPlan, status });

  // Grow the deposit's paid balance by this instalment. Preserve the prior paid
  // history (backfill one `paid` entry when the deposit has no ledger yet) so the
  // ledger-derived balance rises rather than replacing what was already in.
  const deposit = brain.deposits[plan.depositId];
  if (deposit) {
    const priorPaid = depositPaidAud(deposit);
    const baseLedger: DepositLedgerEntry[] =
      deposit.ledger && deposit.ledger.length > 0
        ? deposit.ledger
        : priorPaid > 0
          ? [{ at: deposit.paidAt ?? at, amountAud: priorPaid, kind: 'paid', by: deposit.takenByAgentId ?? 'system' }]
          : [];
    brain.upsertDeposit({
      ...deposit,
      amountPaid: priorPaid + instalment.amountAud,
      ledger: [
        ...baseLedger,
        { at, amountAud: instalment.amountAud, kind: 'topup', by, note: 'Missed top-up chased and paid' },
      ],
    });
  }

  brain.appendActivity({
    id: `act-topup-chase-paid-${plan.id}-${at}`,
    type: 'deposit-paid',
    customerId: plan.customerId,
    studioId: plan.studioId,
    amount: instalment.amountAud,
    body: `Chased top-up paid: $${instalment.amountAud}. Plan back on track.`,
    timestamp: at,
  });
  eventBus.publish('topup:chase-paid', {
    planId: plan.id,
    depositId: plan.depositId,
    amountAud: instalment.amountAud,
    at,
  });
  return { ok: true };
}

/**
 * Reached -> wants to move the date. Returns the split decision:
 *   outside 7d -> 'agent-moves-partial' (the Agent moves it: topupMoveDate holds
 *                 the new date until a >= $50 partial is PAID — B4 75my)
 *   inside 7d  -> 'needs-ops-approval'  (a flagged decision, mechanics TBC)
 * The date move itself is the reschedule flow. Records the decision on the
 * activity feed + publishes `topup:chase-move`.
 */
export function topupChaseMove(
  planId: string,
  opts?: FlowOpts,
): { ok: boolean; mode: 'agent-moves-partial' | 'needs-ops-approval' } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, mode: 'needs-ops-approval' };
  const at = opts?.at ?? nowIso();
  const split = topupSevenDaySplit(plan, at);
  const mode = split === 'outside' ? 'agent-moves-partial' : 'needs-ops-approval';

  brain.appendActivity({
    id: `act-topup-chase-move-${plan.id}-${at}`,
    type: 'outbound-call',
    customerId: plan.customerId,
    studioId: plan.studioId,
    body:
      mode === 'agent-moves-partial'
        ? 'Client wants to move the date (outside 7 days). Agent moves it and takes a partial payment now.'
        : 'Client wants to move the date (inside 7 days). Flagged for Ops approval.',
    timestamp: at,
  });
  eventBus.publish('topup:chase-move', { planId: plan.id, split, mode, at });
  return { ok: true, mode };
}

/** Missed top-up chase sequences run about a day apart (B8 / diagram 05; server: CHASE_GAP_MS). */
export const CHASE_GAP_MS = 20 * 60 * 60 * 1000;

/** When the next "no answer" chase sequence may be logged, or null when it may be now. */
export function nextChaseAt(plan: Pick<TopupPlan, 'lastChaseAt'>, atIso: string): string | null {
  if (!plan.lastChaseAt) return null;
  const next = new Date(plan.lastChaseAt).getTime() + CHASE_GAP_MS;
  return next > new Date(atIso).getTime() ? new Date(next).toISOString() : null;
}

/**
 * A chase sequence went unanswered. Increments chaseAttempts and, on the 3rd
 * (>= MAX_CHASE_SEQUENCES) unanswered sequence, runs cancelMissedTopup (the
 * 7-day split) and reports cancelled. Publishes `topup:chase-no-answer`. A no-op
 * on a plan that is no longer active. Guard: unknown planId -> { ok: false }.
 */
export function topupChaseNoAnswer(
  planId: string,
  opts?: FlowOpts,
): { ok: boolean; attempts: number; cancelled: boolean; completion?: Promise<boolean> } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, attempts: 0, cancelled: false };
  const at = opts?.at ?? nowIso();
  if (plan.status !== 'active') {
    return { ok: true, attempts: plan.chaseAttempts ?? 0, cancelled: plan.status === 'cancelled' };
  }
  // B8 (D05-21 / D02-21): one sequence about a day; the booking stays held meanwhile.
  if (nextChaseAt(plan, at)) return { ok: false, attempts: plan.chaseAttempts ?? 0, cancelled: false };

  const attempts = (plan.chaseAttempts ?? 0) + 1;
  brain.upsertTopupPlan({ ...plan, chaseAttempts: attempts, lastChaseAt: at });
  brain.appendActivity({
    id: `act-topup-chase-noanswer-${plan.id}-${attempts}`,
    type: 'voicemail',
    customerId: plan.customerId,
    studioId: plan.studioId,
    body: `Missed top-up chase, sequence ${attempts} of ${MAX_CHASE_SEQUENCES}: no answer.`,
    timestamp: at,
  });
  eventBus.publish('topup:chase-no-answer', { planId: plan.id, attempts, at });

  if (attempts >= MAX_CHASE_SEQUENCES) {
    const result = cancelMissedTopup(plan.id, { by: opts?.by, at });
    return { ok: result.ok, attempts, cancelled: result.ok, completion: result.completion };
  }
  return { ok: true, attempts, cancelled: false };
}

/**
 * No pickup after the 3rd sequence (or an explicit cancel). Cancels the plan and
 * its booking, then applies the SHARED 7-day split (W5's applyCancelSplit from
 * @atink/domain — the same rule the Ops cancel and its server intent run, so the
 * client can never split a deposit differently):
 *   inside 7d  -> the deposit is FORFEITED ('forfeited' + a forfeit movement; a new
 *                 deposit is needed to rebook)
 *   outside 7d -> kept as a deposit-no-date: unlinked from the booking with a
 *                 12-month on-file expiry, still paid, so the Sweeper pool has it.
 * The booking carries the typed reason / who / when. A dateless plan (no booking)
 * just keeps its deposit on file. Idempotent no-op on an already-cancelled plan.
 * Publishes `topup:cancelled`. Guard: unknown planId -> { ok: false }.
 */
export function cancelMissedTopup(
  planId: string,
  opts?: FlowOpts,
): { ok: boolean; split: 'inside' | 'outside'; completion?: Promise<boolean> } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, split: 'outside' };
  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? currentActorId() ?? 'system';
  const split = topupSevenDaySplit(plan, at);
  if (plan.status === 'cancelled') return { ok: true, split }; // already cancelled

  // W5b #1 (tv-2na.12.17 (e)): through the ONE cancel intent. A plan with a live
  // booking cancels that booking (the shared 7-day split, which cancels the plan
  // and voids its unpaid instalments — Q2); a dateless plan is cancelled on its
  // own and its paid part stays on file (12 months).
  const result = cancelTopupPlanById(plan.id, {
    reason: 'Missed top-up: no pickup after the 3rd chase sequence',
    by,
    source: 'topup-chase',
    at,
  });
  if (!result.ok) return { ok: false, split };

  const recordSuccess = () => {
    brain.appendActivity({
      id: `act-topup-cancelled-${plan.id}-${at}`,
      type: 'booking-cancelled',
      customerId: plan.customerId,
      studioId: plan.studioId,
      body:
        split === 'inside'
          ? 'Top-up chase exhausted inside 7 days: booking cancelled, deposit forfeit.'
          : 'Top-up chase exhausted outside 7 days: booking cancelled, deposit kept on file (12 months).',
      timestamp: at,
    });
    eventBus.publish('topup:cancelled', { planId: plan.id, depositId: plan.depositId, split, at });
  };
  const completion = result.completion?.then((ok) => {
    if (ok) recordSuccess();
    return ok;
  });
  if (!completion) recordSuccess();
  return { ok: true, split, completion };
}

export type TopupMoveOutcome =
  | {
      ok: true;
      ghostBookingId: string;
      partialAud: number;
      lapsesAt: string;
      alreadyPending: boolean;
      /** Card over the phone: the partial is paid and the new date is the booking. */
      landed: boolean;
      /** Pay link: texted to the client (false: no mobile on file, nothing sent). */
      texted?: boolean;
    }
  | { ok: false; insideSevenDays: boolean; message: string };

/**
 * The client wants a new date (B4, 75my; diagram 02; Anas 1 Oct Q4). Outside 7
 * days the Agent moves it: the server holds the new date as a GHOST until a >=
 * $50 partial is paid, and records NO money (this replaced a "Move date" that
 * logged a partial as collected when nothing was paid and never moved the date).
 * Paid, the move lands; unpaid past the link window, both dates are released and
 * the money stays on file. Inside 7 days it's Ops' call (their Top-up extensions
 * list). The ghost is mirrored into the store so the calendar shows it now.
 *
 * L5 (D02-23 / D05-23): the Agent takes the partial then and there (`pay`):
 * 'card-phone' pays it on the call and the move lands at once; 'text-link' has the
 * server text the client a pay link naming the new date, and the move lands when
 * it's paid (the 24h lapse stays the backstop).
 */
export function topupMoveDate(planId: string, newStartIso: string, pay?: TopupMovePay, contactEmail?: string): Promise<TopupMoveOutcome> {
  return topupMoveDateIntent(planId, newStartIso, pay, contactEmail).then((res): TopupMoveOutcome => {
    if (!res.ok) {
      return {
        ok: false,
        insideSevenDays: res.code === 'inside-7-days',
        message: res.message ?? "The date wasn't moved. Try again.",
      };
    }
    const brain = useBrainStore.getState();
    const plan = brain.topupPlans[planId];
    const current = plan?.bookingId ? brain.bookings[plan.bookingId] : undefined;
    if (current && res.ghostBookingId && (res.landed || !brain.bookings[res.ghostBookingId])) {
      const ghost: Booking = {
        ...current,
        id: res.ghostBookingId,
        // The artist's studio, as the server files it (B7).
        studioId: brain.artists[current.artistId]?.studioId ?? current.studioId,
        start: newStartIso,
        status: res.landed ? 'confirmed' : 'held',
        overbookable: false,
        rescheduledFromBookingId: current.id,
        createdAt: useSimulationStore.getState().clockIso,
        confirmationTouches: [],
        confirmedAt: undefined,
        // Landed (card over the phone): the deposit moved onto the new date.
        ...(res.landed ? { depositId: current.depositId } : { depositId: undefined }),
      };
      withServerAuthoredWrites([['bookings', ghost.id]], () => useBrainStore.getState().upsertBooking(ghost));
      if (res.landed) {
        // The old date is superseded by the new one (landPendingMove), as the server holds it.
        const old: Booking = { ...current, status: 'superseded', rescheduledToBookingId: ghost.id };
        withServerAuthoredWrites([['bookings', old.id]], () => useBrainStore.getState().upsertBooking(old));
      }
    }
    // The plan's new schedule (partial now, the rest spread to the new date), so a
    // later write from this tab never carries the old one back. The chase count
    // stays as it is: it starts again only when the partial lands (tv-toef).
    if (plan && res.instalments.length > 0) {
      withServerAuthoredWrites([['topupPlans', plan.id]], () =>
        useBrainStore.getState().upsertTopupPlan({
          ...plan,
          // Landed: the plan is for the new date now (landPendingMove re-points it).
          ...(res.landed && res.ghostBookingId ? { bookingId: res.ghostBookingId } : {}),
          instalments: res.instalments.map((i) => ({
            dueIso: i.dueIso,
            amountAud: i.amountAud,
            status: i.status as TopupInstalment['status'],
            ...(i.paidAt ? { paidAt: i.paidAt } : {}),
          })),
        }),
      );
    }
    // Landed: the deposit sits on the new date (mirrored so a later save from this
    // tab never points it back at the superseded booking). Its money and ledger
    // are the server's and arrive with the live views; nothing is added here.
    const dep = plan ? useBrainStore.getState().deposits[plan.depositId] : undefined;
    // D05-11: a card move filled an empty email from the form server-side.
    if (res.landed && contactEmail) {
      const customerId = plan?.customerId ?? dep?.customerId;
      if (customerId) mirrorCapturedEmail(customerId, contactEmail);
    }
    if (res.landed && dep && res.ghostBookingId) {
      withServerAuthoredWrites([['deposits', dep.id]], () =>
        useBrainStore.getState().upsertDeposit({ ...dep, bookingId: res.ghostBookingId }),
      );
    }
    return {
      ok: true,
      ghostBookingId: res.ghostBookingId,
      partialAud: res.partialAud,
      lapsesAt: res.lapsesAt,
      alreadyPending: res.alreadyPending,
      landed: res.landed,
      ...(res.texted !== undefined ? { texted: res.texted } : {}),
    };
  });
}
